<template>
    <div class="upload-form">
      <vs-collapse type="border" accordion class="p-0">
        <vs-collapse-item ref="uploadFileRow" open>
          <div slot="header">
            <div class="accordion-header">
              <div class="number">1</div>
              <div class="label">{{ `${$t('Upload')} ${$t(modelName)}` }}</div>
              <div class="hint">{{ $t('TypeOfSubmissionRequired') }}</div>
              <div class="hint-expanded" :class="{'complete' : !disableUploadOthers}">
                {{ uploadHint }}
                <font-awesome-icon :icon="['fas', 'check-circle']"/>
              </div>
            </div>
          </div>
          <div class="vx-col w-full">
            <div class="vx-row">
              <div class="vx-col w-full">
                <div class="inner-accordion-hint">Upload Excel Rater</div>
              </div>
              <div class="vx-col w-full">
                <input-file-drag-drop
                    reference="fileTypeDrop"
                    v-model="mainFile"
                    :max-files="maxFiles.file"
                    accepted-files="text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel,application/vnd.ms-excel.sheet.macroEnabled.12"
                />
              </div>
            </div>
          </div>
        </vs-collapse-item>

        <vs-collapse-item ref="uploadOthersRow" class="upload-row" :disabled="disableUploadOthers">
          <div slot="header">
            <div class="accordion-header">
              <div class="number">2</div>
              <div class="label">Upload Other Documents</div>
              <div class="hint">{{ $t('TypeOfSubmissionRequired') }}</div>
              <div class="hint-expanded" :class="{'complete' : !disableUploadOthers}">
                {{ uploadHint }}
                <font-awesome-icon :icon="['fas', 'check-circle']"/>
              </div>
            </div>
          </div>
          <div class="vx-col w-full">
            <div class="vx-row">
              <div class="vx-col w-full">
                <div class="inner-accordion-hint">Upload other documents (CIS, NCD, Claims, Bdx)</div>
              </div>
              <div class="vx-col w-full">
                <input-file-drag-drop
                    reference="otherFilesTypeDrop"
                    v-model="otherFiles"
                    :max-files="maxFiles.otherFiles"
                />
              </div>
            </div>
          </div>
        </vs-collapse-item>
      </vs-collapse>
      <portal to="form-buttons">
        <upload-form-buttons
          :loading="loading || !formComplete"
          @submit="submitForm"
          @reset="resetForm"
        />
      </portal>
    </div>
</template>

<script>
import vSelect from 'vue-select';
import CountryList from 'countries-list';
import UploadFormButtons from "./UploadFormButtons";
import InputFile from "@/views/components/file-upload/InputFile";
import InputFileDragDrop from "@/views/components/file-upload/InputFileDragDrop";
import HasLoadingMixin from '@/components/mixins/HasLoadingMixin';
import RadioLarge from '@/views/components/custom-form-fields/radio-large';
import CheckboxLarge from '@/views/components/custom-form-fields/CheckboxLarge';
import CustomRadioLarge from '@/views/components/custom-form-fields/CustomRadioLarge';

export default {
  name: "FirmOrderForm",
  components: {
    RadioLarge,
    CustomRadioLarge,
    CheckboxLarge,
    vSelect,
    UploadFormButtons,
    InputFile,
    InputFileDragDrop,
  },
  props: {
    modelName: {
      type: String,
      required: true,
    },
    model: {
      type: String,
      required: true,
    },
  },
  mixins: [CountryList, HasLoadingMixin],
  data() {
    return {
      mainFile: [],
      otherFiles: [],
      remoteForm: {},
      standardForm: {},
      maxFiles: {
        file: 1,
        otherFiles: 99,
      },
    }
  },
  computed: {
    uploadText() {
      return this.$t('UploadFormExcel')
    },
    uploadHint() {
      return this.$t('AddYourFilePlural');
    },
    disableUploadOthers() {
        if (!this.mainFile.length
            || this.mainFile.length > this.maxFiles.file
        ) {
          return true;
        }

      return false;
    },
    formComplete() {
      return !this.disableUploadOthers
    },
  },
  methods: {
    clearValidation(field) {
      this.$validator.errors.remove(field);
    },
    addValidationError(field, value) {
      this.clearValidation(field);

      this.$validator.errors.add({
        field: field,
        msg: value,
      })
    },
    async getParams() {
      if (this.mainFile && !this.mainFile.length) {
        this.$vs.notify({
          title: 'Error',
          text: `No files found`,
          iconPack: 'feather',
          icon: 'icon-alert-circle',
          color: 'danger',
          position: "top-right"
        })

        return {};
      }

      const params = new FormData();
      params.append('Language', this.$i18n.locale);
      params.append('QuotationFile', this.mainFile[0]);

      this.otherFiles.forEach((otherFile, i) => {
        params.append(`OtherFiles[${i}]`, otherFile);
      })

      return params;
    },
    async isValid() {
      return this.$validator.validateAll();
    },
    async submitForm() {
      const params = await this.getParams();
      const valid = await this.isValid();

      if (!valid) {
        return false;
      }

      try {
        this.enableLoading();
        const { data } = await this.$api[this.model].create(params);

        const { ClientReference, Message } = data.data;

        const success = (ClientReference) ? true : false;

        if (success) {
          await this.$router.push({name: `${this.$route.name}.success`, params: { clientReference: ClientReference, message: Message }})
        } else {
          await this.$router.push({name: `${this.$route.name}.error`})
        }

      } catch(e) {
        const { Message, Errors } = e.response.data.data;
        if (Errors) {
          this.addErrors(e);
        }
        this.$vs.notify({
          title: this.$t('ErrorOccurred'),
          text: (Message) ? Message : this.$t('UploadFailureMessage'),
          iconPack: 'feather',
          icon: 'icon-alert-circle',
          color: 'danger',
          position: 'top-right'
        });
      } finally {
        this.disableLoading();
      }
    },
    resetForm() {
      this.mainFile = [];
      this.otherFiles = [];
      this.$validator.reset();
      this.errors.clear();
    },
    addErrors(e) {
      if (!e.response.data.data.Errors) {
        return;
      }

      const errorBag = Object.entries(e.response.data.data.Errors)
          .filter((item) => item[1] !== null)
          .map((errorBag) => {
            return {
              field: errorBag[0],
              msg: errorBag[1][0]
            };
          });

      const addErrorToForms = (component, forms, {field, msg}) => {
        component.$validator.errors.add();

        forms.forEach((ref) => {
          const child = component.$refs[ref];

          if (!child) {
            return true;
          }

          // Child forms
          const childForms = child.$data['forms'] || [];
          addErrorToForms(child, childForms, {field, msg});
        });
      };

      errorBag.forEach((error) => {
        addErrorToForms(this, this.forms || [], error);
      });
    },
  }
}
</script>

<style lang="scss">
@import '@/assets/scss/custom/components/variables';
  .vs-input--label {
    font-size: 0.9rem;

    &.text-large {
      font-size: 1.2rem;
    }
  }

  .upload-form {
    .multiselect__content-wrapper {
      z-index: 999;
    }

    .vs-collapse-item {
      cursor: initial;

      .accordion-header {
        display: flex;
        flex-direction: row;
        align-items: center;

        .number {
          display: inline-block;
          width: 22px;
          height: 22px;
          background-color: $yellow;
          text-align: center;
          border-radius: 50%;
          font-size: 15px;
          font-weight: bold;
          color: #000;
        }

        .label {
          display: inline-block;
          margin-left: 11px;
          font-size: 22px;
          color: #262626;
        }

        .hint,
        .hint-expanded {
          margin-left: auto;
          padding-right: 15px;
          font-size: 14px;
        }

        .hint {
          display: none;
        }

        .hint-expanded {
          &.complete {
            font-weight: bold;

            > svg {
              display: initial;
            }
          }

          > svg {
            display: none;
            color: $green;
          }
        }
      }

      .inner-accordion-hint {
        font-size: 15px;
        color: #262626;
      }

      .vs-collapse-item--content {
        .con-content--item {
          padding-top: 0;
        }
      }

      &.open-item {
        .vs-collapse-item--content {
          max-height: initial !important;
        }
      }

      &.upload-row {
        &.open-item {
          .vs-collapse-item--content {
            max-height: 400px !important;
          }
        }
      }

      &.email-address-row {
        &.open-item {
          .vs-collapse-item--content {
            overflow: visible;
            max-height: 400px !important;

            @media (max-width: 1199px) {
              max-height: 8000px !important;
            }
          }
        }

        .additional-emails {
          display: flex;
          flex-direction: row;
          flex-wrap: wrap;
          justify-content: flex-start;
          align-items: center;

          .additional-email {
            display: flex;
            flex-direction: row;
            align-items: center;
            justify-content: space-between;
            border: 1px solid $grey-light;
            border-radius: 5px;
            font-size: 13px;
            margin-top: 6px;
            margin-right: 6px;

            .email {
              padding: 6px 12px;
              background-color: $grey-lightest;
            }

            .remove-button {
              width: 25px;
              height: 31px;
              display: flex;
              align-items: center;
              justify-content: center;
              border-left: 1px solid $grey-light;

              &:hover {
                background-color: $grey-lightest;
              }
            }
          }
        }
      }

      &.disabledx {
        opacity: 1;

        .number {
          color: rgba(68, 75, 87, 0.7);
          background-color: rgba(38, 38, 38, 0.2);
        }

        .hint,
        .hint-expanded {
          color: rgba(68, 75, 87, 0.7);
        }

        .hint {
          display: initial;
        }

        .hint-expanded {
          display: none;
        }
      }
    }
  }
</style>
